// ref: https://github.com/chakra-ui/chakra-ui/blob/main/packages/hooks/use-disclosure/src/index.ts
// * modified to also accept data

import { useCallback, useId, useState } from 'react';

export interface UseDisclosureProps<T> {
  id?: string;
  defaultIsOpen?: boolean;
  initialData?: T;
  resetDataOnClose?: boolean;
  autoCloseMs?: number;
}

export function useDisclosure<T = null>(props: UseDisclosureProps<T> = {}) {
  const { id: idProp, defaultIsOpen = false, initialData, resetDataOnClose = true, autoCloseMs = 0 } = props;

  const [data, setData] = useState<T | undefined>(initialData);
  const [isOpen, setIsOpen] = useState(defaultIsOpen);

  const uid = useId();
  const id = idProp ?? `disclosure-${uid}`;

  const onClose = useCallback(() => {
    if (resetDataOnClose) {
      setData(undefined);
    }
    setIsOpen(false);
  }, [resetDataOnClose]);

  const onOpen = useCallback(
    (data?: T) => {
      setData(data);
      setIsOpen(true);
      if (autoCloseMs > 0) {
        setTimeout(() => {
          onClose();
        }, autoCloseMs);
      }
    },
    [autoCloseMs, onClose]
  );

  const onToggle = useCallback(
    (data?: T) => {
      if (isOpen) {
        if (resetDataOnClose) {
          setData(undefined);
        }
        onClose();
      } else {
        setData(data);
        onOpen();
      }
    },
    [isOpen, onOpen, onClose, resetDataOnClose]
  );

  return {
    id,
    isOpen,
    onOpen,
    onClose,
    onToggle,
    data,
    setData,
  };
}

export type UseDisclosureReturn<T> = ReturnType<typeof useDisclosure<T>>;
